import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, FormActions, TimeSlotSelector, HorizontalDatePicker } from "../../shared";
import { Select, SelectOption } from "../../shared/Select";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import {
  appointmentsApi,
  clientsApi,
  servicesApi,
  professionalsApi,
  availabilityApi,
} from "../../../services";
import { CreateClientModal } from "../AdminClients/CreateClientModal";
import {
  Client,
  Service,
  Professional,
  BookingData,
  ServiceType,
  TimeSlot,
  Car,
} from "../../../types";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import { formatTime } from "../../../utils/dateUtils";
import { resolvePriceForCarEffort, formatServiceSelectLabel } from "../../../utils/serviceEffortPrice";

interface CreateAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedDate: string;
  preFilledData?: {
    professionalId?: number;
    date?: string;
    time?: string;
    clientId?: number;
    serviceId?: number;
    clientCarId?: number;
    price?: string;
    subServiceIds?: number[];
    lockPrice?: boolean;
    budgetId?: number;
  };
  budgetQueueProgress?: { current: number; total: number };
  onSkipRemaining?: () => void;
  editingAppointment?: {
    id: number;
    clientId: number;
    serviceId: number;
    professionalId?: number;
    appointmentDate: string;
    startTime?: string;
    price?: string;
    clientCarId?: number;
  } | null;
}

interface AppointmentFormData {
  clientId: string;
  serviceId: string;
  professionalId?: string;
  date: string;
  time: string;
  notes?: string;
  price?: string;
  clientCarId?: string;
}

export const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedDate,
  preFilledData,
  budgetQueueProgress,
  onSkipRemaining,
  editingAppointment,
}) => {
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;

  const [formData, setFormData] = useState<AppointmentFormData>({
    clientId: "",
    serviceId: "",
    professionalId: preFilledData?.professionalId?.toString() || (loggedInProfessionalId ? loggedInProfessionalId.toString() : ""),
    date: preFilledData?.date || selectedDate,
    time: preFilledData?.time || "",
    notes: "",
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [clientCars, setClientCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [originalAppointmentData, setOriginalAppointmentData] = useState<{
    clientId: number;
    serviceId: number;
    professionalId?: number;
    appointmentDate: string;
    startTime?: string;
    price?: string;
    clientCarId?: number;
  } | null>(null);
  const previousServiceIdRef = useRef<string>("");
  const previousClientCarIdRef = useRef<string>("");
  const lockPriceRef = useRef(false);
  const prefilledSubServiceIdsRef = useRef<number[]>([]);

  const fetchClientCars = useCallback(async (clientId: number) => {
    try {
      const response = await clientsApi.getCarsByClient(clientId);
      if (response.success && response.data) {
        setClientCars(response.data);
      }
    } catch (err) {
      console.error("Error fetching client cars:", err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!company?.id) return;

    try {
      setLoading(true);

      // Fetch clients
      const clientsResponse = await clientsApi.getClientsByCompany();
      if (clientsResponse.success && clientsResponse.data) {
        setClients(clientsResponse.data);
      }

      // Fetch services (only for car companies, barbershop will fetch by professional)
      if (company.category === "car") {
        const servicesResponse = await servicesApi.getServicesByCompany();
        if (servicesResponse.success && servicesResponse.data) {
          setServices(servicesResponse.data);
        }
      }

      // Fetch professionals if barbershop
      if (company.category === "barbershop") {
        // If user is a professional, fetch only their own data
        if (isProfessional && loggedInProfessionalId) {
          const professionalResponse = await professionalsApi.getProfessionalById(loggedInProfessionalId);
          if (professionalResponse.success && professionalResponse.data) {
            setProfessionals([professionalResponse.data]);
            // Set as default if not already set
            setFormData((prev) => {
              if (prev.professionalId) return prev;
              return {
                ...prev,
                professionalId: loggedInProfessionalId.toString(),
              };
            });
          }
        } else {
          // If user is company admin, fetch all professionals
          const professionalsResponse =
            await professionalsApi.getProfessionalsByCompany();
          if (professionalsResponse.success && professionalsResponse.data) {
            setProfessionals(
              professionalsResponse.data.filter((p) => p.isActive !== false)
            );
          }
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [company?.id, company?.category, isProfessional, loggedInProfessionalId]);

  useEffect(() => {
    if (isOpen && company?.id) {
      fetchData();
      // If editing, populate form with appointment data
      if (editingAppointment) {
        lockPriceRef.current = false;
        prefilledSubServiceIdsRef.current = [];
        const defaultProfessionalId = editingAppointment.professionalId?.toString() ||
          (loggedInProfessionalId ? loggedInProfessionalId.toString() : "");

        setFormData({
          clientId: editingAppointment.clientId.toString(),
          serviceId: editingAppointment.serviceId.toString(),
          professionalId: defaultProfessionalId,
          date: editingAppointment.appointmentDate,
          time: editingAppointment.startTime || "",
          notes: "",
          price: editingAppointment.price || "",
          clientCarId: editingAppointment.clientCarId?.toString() || "",
        });
        
        // Store original data for comparison
        setOriginalAppointmentData({
          clientId: editingAppointment.clientId,
          serviceId: editingAppointment.serviceId,
          professionalId: editingAppointment.professionalId,
          appointmentDate: editingAppointment.appointmentDate,
          startTime: editingAppointment.startTime,
          price: editingAppointment.price,
          clientCarId: editingAppointment.clientCarId,
        });
        previousServiceIdRef.current = editingAppointment.serviceId.toString();
        previousClientCarIdRef.current =
          editingAppointment.clientCarId?.toString() || "";
        
        // Fetch client cars if editing and company is car
        if (company?.category === "car" && editingAppointment.clientId) {
          fetchClientCars(editingAppointment.clientId);
        }
        
        // For barbershop, services will be fetched by the useEffect when professionalId is set
        // The serviceId is already set in formData above, so it will be preserved
      } else {
        // Reset form when modal opens, but use pre-filled data if provided
        // If user is professional, default to their ID
        const defaultProfessionalId = preFilledData?.professionalId?.toString() || 
          (loggedInProfessionalId ? loggedInProfessionalId.toString() : "");
        setFormData({
          clientId: preFilledData?.clientId?.toString() || "",
          serviceId: preFilledData?.serviceId?.toString() || "",
          professionalId: defaultProfessionalId,
          date: preFilledData?.date || selectedDate,
          time: preFilledData?.time || "",
          notes: "",
          price: preFilledData?.price || "",
          clientCarId: preFilledData?.clientCarId?.toString() || "",
        });
        previousServiceIdRef.current = preFilledData?.serviceId?.toString() || "";
        previousClientCarIdRef.current =
          preFilledData?.clientCarId?.toString() || "";
        lockPriceRef.current = Boolean(preFilledData?.lockPrice);
        prefilledSubServiceIdsRef.current = preFilledData?.subServiceIds ?? [];

        if (company?.category === "car" && preFilledData?.clientId) {
          fetchClientCars(preFilledData.clientId);
        }
      }
      setTimeSlots([]);
      setSelectedTimeSlot(null);
      setError(null);
      setSlotsError(null);
      if (!editingAppointment) {
        setOriginalAppointmentData(null);
      }
    }
  }, [
    isOpen,
    company?.id,
    company?.category,
    selectedDate,
    preFilledData,
    loggedInProfessionalId,
    editingAppointment,
    fetchData,
    fetchClientCars,
  ]);

  // Fetch available slots when date, service, and professional are selected (for barbershop)
  useEffect(() => {
    if (
      company?.category === "barbershop" &&
      formData.date &&
      formData.serviceId &&
      formData.professionalId &&
      isOpen
    ) {
      const fetchSlots = async () => {
        setLoadingSlots(true);
        setSlotsError(null);
        setSelectedTimeSlot(null);
        setTimeSlots([]);

        const date = formData.date;
        const serviceId = formData.serviceId;
        const professionalId = formData.professionalId;

        if (!date || !serviceId || !professionalId) {
          setLoadingSlots(false);
          return;
        }

        try {
          const response = await availabilityApi.getAvailableSlotsByProfessional(
            parseInt(professionalId),
            parseInt(serviceId),
            date
          );

          if (response.success && response.data) {
            setTimeSlots(response.data);
            // If we have a pre-filled time or editing with original time, select the matching slot
            const timeToMatch = formData.time || (editingAppointment && originalAppointmentData?.startTime);
            if (timeToMatch) {
              const matchingSlot = response.data.find(
                (slot: TimeSlot) => slot.startTime === timeToMatch
              );
              if (matchingSlot) {
                setSelectedTimeSlot(matchingSlot);
              }
            }
          } else {
            setSlotsError(response.error || "Erro ao carregar horários");
          }
        } catch (err) {
          console.error("Error fetching slots:", err);
          setSlotsError("Erro ao carregar horários disponíveis");
        } finally {
          setLoadingSlots(false);
        }
      };

      fetchSlots();
    } else if (company?.category === "car") {
      // Reset slots for car companies
      setTimeSlots([]);
      setSelectedTimeSlot(null);
    }
  }, [
    formData.date,
    formData.serviceId,
    formData.professionalId,
    formData.time,
    company?.category,
    isOpen,
    editingAppointment,
    originalAppointmentData?.startTime,
  ]);

  // Update formData.time when a slot is selected
  useEffect(() => {
    if (selectedTimeSlot?.startTime) {
      setFormData((prev) => ({ ...prev, time: selectedTimeSlot.startTime || "" }));
    }
  }, [selectedTimeSlot]);

  // Fetch cars when client is selected (for car companies)
  useEffect(() => {
    if (
      company?.category === "car" &&
      formData.clientId &&
      editingAppointment &&
      parseInt(formData.clientId) === editingAppointment.clientId
    ) {
      fetchClientCars(parseInt(formData.clientId));
    } else if (company?.category === "car" && formData.clientId && !editingAppointment) {
      fetchClientCars(parseInt(formData.clientId));
    } else if (!formData.clientId) {
      setClientCars([]);
    }
  }, [formData.clientId, company?.category, editingAppointment, fetchClientCars]);

  // Fetch services by professional when professional is selected (for barbershop)
  useEffect(() => {
    if (
      company?.category === "barbershop" &&
      formData.professionalId &&
      isOpen
    ) {
      const fetchProfessionalServices = async () => {
        const professionalId = formData.professionalId;
        if (!professionalId) {
          return;
        }

        try {
          setLoading(true);
          const response = await servicesApi.getServicesByProfessional(
            parseInt(professionalId)
          );

          if (response.success && response.data) {
            setServices(response.data);
            // When editing, set serviceId after services are loaded
            if (editingAppointment) {
              // Ensure serviceId is set after services are loaded
              setFormData((prev) => ({
                ...prev,
                serviceId: editingAppointment.serviceId.toString(),
              }));
            } else if (preFilledData?.serviceId) {
              setFormData((prev) => ({
                ...prev,
                serviceId: preFilledData.serviceId!.toString(),
              }));
              previousServiceIdRef.current = preFilledData.serviceId!.toString();
            } else {
              setFormData((prev) => ({ ...prev, serviceId: "" }));
              setSelectedTimeSlot(null);
              setTimeSlots([]);
            }
          } else {
            setError(response.error || "Erro ao carregar serviços");
          }
        } catch (err) {
          console.error("Error fetching professional services:", err);
          setError("Erro ao carregar serviços do profissional");
        } finally {
          setLoading(false);
        }
      };

      fetchProfessionalServices();
    } else if (company?.category === "barbershop" && !formData.professionalId) {
      // Reset services when professional is cleared
      setServices([]);
      setFormData((prev) => ({ ...prev, serviceId: "" }));
    }
  }, [formData.professionalId, company?.category, isOpen, editingAppointment, preFilledData]);

  // Sync price from service (barbershop: on service change only). Car: service + vehicle effort tiers.
  useEffect(() => {
    if (!formData.serviceId || services.length === 0) return;
    const selectedService = services.find(
      (s) => s.id.toString() === formData.serviceId
    );
    if (selectedService?.price == null || selectedService.price === "") return;

    const serviceChanged = previousServiceIdRef.current !== formData.serviceId;
    const currentCarId = formData.clientCarId?.trim() || "";
    const carChanged = previousClientCarIdRef.current !== currentCarId;

    if (serviceChanged) {
      lockPriceRef.current = false;
    }
    if (lockPriceRef.current) return;

    if (company?.category === "barbershop") {
      if (!serviceChanged) return;
      previousServiceIdRef.current = formData.serviceId;
      setFormData((prev) => ({
        ...prev,
        price: selectedService.price,
      }));
      return;
    }

    if (company?.category === "car") {
      // Wait until cars are loaded before resolving effort-based prices for a selected car
      if (currentCarId && clientCars.length === 0) {
        return;
      }

      // Only overwrite price when service or car actually changed — not when lists finish loading
      // (preserves custom price when editing date/time only).
      if (!serviceChanged && !carChanged) {
        return;
      }

      if (serviceChanged) {
        previousServiceIdRef.current = formData.serviceId;
      }
      if (carChanged) {
        previousClientCarIdRef.current = currentCarId;
      }

      const car = currentCarId
        ? clientCars.find((c) => c.id.toString() === currentCarId)
        : undefined;
      const nextPrice = resolvePriceForCarEffort(selectedService, car?.effort);
      setFormData((prev) => {
        if (prev.price === nextPrice) return prev;
        return { ...prev, price: nextPrice };
      });
      return;
    }

    if (serviceChanged) {
      previousServiceIdRef.current = formData.serviceId;
      setFormData((prev) => ({
        ...prev,
        price: selectedService.price,
      }));
    }
  }, [
    formData.serviceId,
    formData.clientCarId,
    services,
    clientCars,
    company?.category,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !company?.id ||
      !formData.clientId ||
      !formData.serviceId ||
      !formData.date
    ) {
      setError("Preencha todos os campos obrigatórios");
      return;
    }

    if (company.category === "barbershop") {
      if (!formData.professionalId) {
        setError("Selecione um profissional");
        return;
      }
      // Time is only required when creating, not when editing
      if (!editingAppointment && !selectedTimeSlot?.startTime) {
        setError("Selecione um horário disponível");
        return;
      }
    } 

    setLoading(true);
    setError(null);

    try {
      // Get startTime from selectedTimeSlot for barbershop, or formData.time for car
      // When editing, use original time if no new time is selected
      let startTime: string | undefined;
      if (company.category === "barbershop") {
        startTime = selectedTimeSlot?.startTime;
        // If editing and no time selected, use original time
        if (editingAppointment && !startTime && originalAppointmentData?.startTime) {
          startTime = originalAppointmentData.startTime;
        }
      } else {
        startTime = formData.time;
        // If editing and no time selected, use original time
        if (editingAppointment && !startTime && originalAppointmentData?.startTime) {
          startTime = originalAppointmentData.startTime;
        }
      }

      // Determine professionalId: use formData if available, otherwise use logged-in professional ID, or default to 1
      const professionalId = formData.professionalId
        ? parseInt(formData.professionalId)
        : undefined;

      const appointmentData: BookingData = {
        clientId: parseInt(formData.clientId),
        serviceId: parseInt(formData.serviceId),
        professionalId,
        date: formData.date,
        serviceType: company.category === "barbershop" ? ServiceType.TIME : ServiceType.DAY,
        ...(company.category === "barbershop" && startTime && {
          startTime: startTime,
        }),
        ...(formData.price?.trim() && { price: formData.price.trim() }),
        ...((prefilledSubServiceIdsRef.current.length > 0 ||
          (preFilledData?.subServiceIds?.length ?? 0) > 0) && {
          subServiceIds:
            prefilledSubServiceIdsRef.current.length > 0
              ? prefilledSubServiceIdsRef.current
              : preFilledData!.subServiceIds,
        }),
        ...(company.category === "car" &&
          formData.clientCarId?.trim() && {
            clientCarId: parseInt(formData.clientCarId),
          }),
      };

      if (editingAppointment && originalAppointmentData) {
        // Update existing appointment - only include changed fields
        const updateData: any = {};
        
        // Check if client changed
        const newClientId = parseInt(formData.clientId);
        if (newClientId !== originalAppointmentData.clientId) {
          updateData.clientId = newClientId;
        }
        
        // Check if service changed
        const newServiceId = parseInt(formData.serviceId);
        if (newServiceId !== originalAppointmentData.serviceId) {
          updateData.serviceId = newServiceId;
        }
        
        // Check if professional changed
        if (professionalId !== originalAppointmentData.professionalId) {
          updateData.professionalId = professionalId;
        }
        
        // Check if date changed
        if (formData.date !== originalAppointmentData.appointmentDate) {
          updateData.appointmentDate = formData.date;
        }
        
        // Check if time changed
        if (startTime && startTime !== originalAppointmentData.startTime) {
          updateData.startTime = startTime;
        }
        // If no time is selected when editing, keep the original time (don't include in updateData)

        // Check if price changed (normalize so "150" vs "150.00" is not treated as a change)
        const newPrice = formData.price?.trim() || "";
        const originalPrice = originalAppointmentData.price || "";
        const normalizePrice = (value: string) => {
          const n = parseFloat(value.replace(",", "."));
          return Number.isFinite(n) ? n.toFixed(2) : value.trim();
        };
        if (normalizePrice(newPrice) !== normalizePrice(originalPrice)) {
          updateData.price = newPrice || null;
        }

        // Check if car changed (for car companies)
        if (company.category === "car") {
          const newCarId = formData.clientCarId?.trim() 
            ? parseInt(formData.clientCarId) 
            : null;
          const originalCarId = originalAppointmentData.clientCarId || null;
          
          if (newCarId !== originalCarId) {
            updateData.clientCarId = newCarId;
          }
        }

        // Only update if there are changes
        if (Object.keys(updateData).length === 0) {
          setError("Nenhuma alteração foi feita");
          setLoading(false);
          return;
        }

        const response = await appointmentsApi.updateAppointment(
          editingAppointment.id,
          updateData
        );

        if (response.success) {
          onSuccess();
        } else {
          setError(response.error || "Erro ao atualizar agendamento");
        }
      } else {
        // Create new appointment
        const response = await appointmentsApi.bookAppointment(appointmentData);

        if (response.success) {
          onSuccess();
        } else {
          setError(response.error || "Erro ao criar agendamento");
        }
      }
    } catch (err) {
      console.error(`Error ${editingAppointment ? "updating" : "creating"} appointment:`, err);
      setError(`Erro ao ${editingAppointment ? "atualizar" : "criar"} agendamento`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof AppointmentFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  // Convert clients to SelectOption format
  const clientOptions: SelectOption[] = useMemo(() => {
    return clients.map((client) => ({
      value: client.id.toString(),
      label: `${client.name} ${client.phoneNumber ? ' - ' + formatPhoneNumber(client.phoneNumber) : "" as string}`,
    }));
  }, [clients]);

  // Check if search term doesn't match any client
  const clientNotFound = useMemo(() => {
    if (!clientSearchTerm || clientSearchTerm.trim() === "") return false;
    const searchLower = clientSearchTerm.toLowerCase().trim();
    return !clients.some((client) =>
      client.name.toLowerCase().includes(searchLower)
    );
  }, [clientSearchTerm, clients]);

  // Capitalize first letter of each word
  const capitalizeName = (name: string): string => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Handle creating client with search term
  const handleCreateClientFromSearch = async () => {
    if (!clientSearchTerm || clientSearchTerm.trim() === "") return;

    setCreatingClient(true);
    setError(null);

    try {
      const capitalizedName = capitalizeName(clientSearchTerm);
      const response = await clientsApi.createClient({
        name: capitalizedName,
      });

      if (response.success && response.data) {
        // Refresh clients list
        await fetchData();
        // Auto-select the newly created client
        const createdClient = response.data;
        if (createdClient && createdClient.id) {
          setFormData((prev) => ({
            ...prev,
            clientId: createdClient.id.toString(),
          }));
        }
        setClientSearchTerm("");
      } else {
        setError(response.error || "Erro ao criar cliente");
      }
    } catch (err) {
      console.error("Error creating client:", err);
      setError("Erro ao criar cliente");
    } finally {
      setCreatingClient(false);
    }
  };

  // Convert services to SelectOption format
  const serviceOptions: SelectOption[] = useMemo(() => {
    return services.map((service) => ({
      value: service.id.toString(),
      label: formatServiceSelectLabel(service),
    }));
  }, [services]);

  // Convert professionals to SelectOption format
  const professionalOptions: SelectOption[] = useMemo(() => {
    return professionals.map((professional) => ({
      value: professional.id.toString(),
      label: professional.name,
    }));
  }, [professionals]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        editingAppointment
          ? "Editar Agendamento"
          : preFilledData?.budgetId
            ? budgetQueueProgress && budgetQueueProgress.total > 1
              ? `Agendar — Orçamento #${preFilledData.budgetId} (${budgetQueueProgress.current}/${budgetQueueProgress.total})`
              : `Agendar — Orçamento #${preFilledData.budgetId}`
            : "Novo Agendamento"
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {budgetQueueProgress && budgetQueueProgress.total > 1 && (
          <div className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2.5 text-sm text-sky-800">
            Agendamento <strong>{budgetQueueProgress.current}</strong> de{" "}
            <strong>{budgetQueueProgress.total}</strong> deste orçamento. Após
            salvar, o próximo serviço será aberto automaticamente.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client Selection */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Cliente
              </label>
              <button
                type="button"
                onClick={() => setCreateClientModalOpen(true)}
                className="text-xs px-2 py-1 bg-sky-500 hover:bg-sky-600 text-white rounded-md transition-colors duration-200 flex items-center gap-1"
                title="Criar novo cliente"
              >
                <span>+</span>
                <span>Novo Cliente</span>
              </button>
            </div>
            <Select
              value={formData.clientId}
              onChange={(value: string | number) => {
                handleInputChange("clientId", value.toString());
                setClientSearchTerm("");
              }}
              onSearchChange={(searchTerm) => setClientSearchTerm(searchTerm)}
              options={clientOptions}
              placeholder="Selecione um cliente"
              required
              searchable={true}
              searchPlaceholder="Buscar cliente..."
              disabled={loading}
            />
            {/* Show option to create client from search when not found */}
            {clientNotFound && clientSearchTerm.trim() && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700 mb-2">
                  Cliente não encontrado: <span className="font-semibold">"{clientSearchTerm}"</span>
                </p>
                <button
                  type="button"
                  onClick={handleCreateClientFromSearch}
                  disabled={creatingClient || loading}
                  className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {creatingClient ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      <span>Criando...</span>
                    </>
                  ) : (
                    <>
                      <span>+</span>
                      <span>Criar cliente com este nome</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Service Selection - Only for car companies */}
          {company?.category === "car" && (
            <div>
              <Select
                label="Serviço"
                value={formData.serviceId}
                onChange={(value: string | number) => handleInputChange("serviceId", value.toString())}
                options={serviceOptions}
                placeholder="Selecione um serviço"
                required
                searchable={true}
                searchPlaceholder="Buscar serviço..."
                disabled={loading}
              />
            </div>
          )}

          {/* Professional Selection - Only for barbershop companies, in same grid as client */}
          {company?.category === "barbershop" && (
            <div>
              <Select
                label="Profissional"
                value={formData.professionalId || ""}
                onChange={(value: string | number) =>
                  handleInputChange("professionalId", value.toString())
                }
                options={professionalOptions}
                placeholder="Selecione um profissional"
                required
                searchable={!isProfessional}
                searchPlaceholder="Buscar profissional..."
                disabled={loading || isProfessional}
              />
            </div>
          )}
        </div>

        {/* Service Selection - Show after professional for barbershop */}
        {company?.category === "barbershop" && formData.professionalId && (
          <div>
            <Select
              label="Serviço"
              value={formData.serviceId}
              onChange={(value: string | number) => handleInputChange("serviceId", value.toString())}
              options={serviceOptions}
              placeholder="Selecione um serviço"
              required
              searchable={true}
              searchPlaceholder="Buscar serviço..."
              disabled={loading || !formData.professionalId}
            />
          </div>
        )}

        {/* Date */}
        <div>
          <HorizontalDatePicker
            selectedDate={formData.date}
            onDateChange={(date) => handleInputChange("date", date)}
            disablePast={false}
            showBorder={false}
          />
        </div>

        {/* Time Slots - Show for barbershop companies, but hide if time is pre-filled from calendar */}
        {company?.category === "barbershop" &&
          formData.date &&
          formData.serviceId &&
          formData.professionalId &&
          !preFilledData?.time && (
            <TimeSlotSelector
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={(slot) => {
                setSelectedTimeSlot(slot);
                if (error) setError(null);
              }}
              loading={loadingSlots}
              error={slotsError}
              label="Horário"
            />
          )}

        {/* Display pre-filled time as read-only when coming from calendar */}
        {company?.category === "barbershop" &&
          preFilledData?.time && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horário
              </label>
              <div className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                {formatTime(preFilledData.time)}
              </div>
            </div>
          )}

        {/* Price - when a service is selected (create or edit); defaults to service price, user can overwrite */}
        {formData.serviceId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preço (R$)
            </label>
            <input
              type="text"
              value={formData.price || ""}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d.,]/g, "");
                handleInputChange("price", value);
              }}
              placeholder="0.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Car Selection - car companies when client is selected */}
        {company?.category === "car" && formData.clientId && (
          <div>
            <Select
              label="Veículo"
              value={formData.clientCarId || ""}
              onChange={(value: string | number) =>
                handleInputChange("clientCarId", value.toString())
              }
              options={[
                { value: "", label: "Nenhum veículo" },
                ...clientCars.map((car) => ({
                  value: car.id.toString(),
                  label: `${car.model} - ${car.plate}`,
                })),
              ]}
              placeholder="Selecione um veículo"
              searchable={false}
              disabled={loading || !formData.clientId}
            />
          </div>
        )}

        {/* Form Actions */}
        {onSkipRemaining && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSkipRemaining}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Pular serviços restantes
            </button>
          </div>
        )}
        <FormActions
          onCancel={onClose}
          onSubmit={() => {}}
          loading={loading}
          submitText={editingAppointment ? "Salvar Alterações" : "Criar Agendamento"}
          submitType="submit"
          isEditing={!!editingAppointment}
        />
      </form>

      {/* Create Client Modal */}
      <CreateClientModal
        isOpen={createClientModalOpen}
        onClose={() => setCreateClientModalOpen(false)}
        client={null}
        onSuccess={(createdClient) => {
          // Refresh clients list
          fetchData();
          // Auto-select the newly created client
          if (createdClient && createdClient.id) {
            setFormData((prev) => ({
              ...prev,
              clientId: createdClient.id.toString(),
            }));
          }
          setCreateClientModalOpen(false);
        }}
      />
    </Modal>
  );
};
